import React, { useCallback, useEffect, useState } from "react";
import { AlertCircle, Download, RefreshCw, Scale } from "lucide-react";

import api from "../api";
import { printReport } from "./reportPrint";

const EMPTY_REPORT = {
  accounts: [],
  total_debit: 0,
  total_credit: 0,
  difference: 0,
  is_balanced: true,
};

function amount(value) {
  return `Rs. ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function TrialBalance() {
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(EMPTY_REPORT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadReport = useCallback(async (selectedDate = asOfDate) => {
    setLoading(true);
    try {
      const response = await api.get(`/accounts/reports/trial-balance?as_of_date=${selectedDate}`);
      setReport(response.data || EMPTY_REPORT);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load trial balance.");
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => {
    loadReport(asOfDate);
  }, [asOfDate, loadReport]);

  const downloadPdf = () => {
    printReport({
      title: "Trial Balance",
      subtitle: `As of ${report.as_of_date || asOfDate}`,
      summary: [
        { label: "Total Debit", value: amount(report.total_debit) },
        { label: "Total Credit", value: amount(report.total_credit) },
        { label: "Status", value: report.is_balanced ? "Balanced" : `Difference ${amount(report.difference)}` },
      ],
      tables: [
        {
          title: "Accounts",
          columns: ["Code", "Account", "Type", "Subtype", "Opening", "Debit", "Credit"],
          rows: (report.accounts || []).map((row) => [
            row.account_code,
            row.account_name,
            row.account_type,
            row.account_subtype || "-",
            amount(row.opening_balance),
            amount(row.debit),
            amount(row.credit),
          ]),
        },
      ],
    });
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-5">
        <div>
          <h3 className="text-xl font-bold text-slate-900">Trial Balance</h3>
          <p className="text-sm text-slate-500">Auto-generated from posted GL entries with debit and credit balancing.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={asOfDate} onChange={(event) => setAsOfDate(event.target.value)} className="input" />
          <button type="button" onClick={() => loadReport(asOfDate)} className="secondary-btn">
            <RefreshCw size={15} />
            Generate
          </button>
          <button type="button" onClick={downloadPdf} className="secondary-btn">
            <Download size={15} />
            Download as PDF
          </button>
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      <div className="grid gap-4 border-b border-slate-100 px-6 py-5 md:grid-cols-3">
        <StatCard label="Total Debit" value={report.total_debit} />
        <StatCard label="Total Credit" value={report.total_credit} />
        <div className={`rounded-2xl border p-4 ${report.is_balanced ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          <div className="text-xs font-semibold uppercase tracking-wide">Status</div>
          <div className="mt-2 text-lg font-bold">{report.is_balanced ? "Balanced" : `Difference ${amount(report.difference)}`}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Account</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Subtype</th>
              <th className="px-4 py-3">Opening</th>
              <th className="px-4 py-3">Debit</th>
              <th className="px-4 py-3">Credit</th>
            </tr>
          </thead>
          <tbody>
            {!loading && (report.accounts || []).map((row) => (
              <tr key={row.account_code} className="border-t border-slate-100">
                <td className="px-4 py-3 font-semibold text-indigo-700">{row.account_code}</td>
                <td className="px-4 py-3">{row.account_name}</td>
                <td className="px-4 py-3">{row.account_type}</td>
                <td className="px-4 py-3">{row.account_subtype || "-"}</td>
                <td className="px-4 py-3">{amount(row.opening_balance)}</td>
                <td className="px-4 py-3">{amount(row.debit)}</td>
                <td className="px-4 py-3">{amount(row.credit)}</td>
              </tr>
            ))}
            {!loading && !(report.accounts || []).length && (
              <tr>
                <td colSpan="7" className="px-4 py-10 text-center text-slate-500">No trial balance rows available.</td>
              </tr>
            )}
            {loading && (
              <tr>
                <td colSpan="7" className="px-4 py-10 text-center text-slate-500">Loading trial balance...</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <style>{`
        .input {
          border-radius: 0.75rem;
          border: 1px solid rgb(203 213 225);
          padding: 0.65rem 0.85rem;
          background: white;
        }
        .secondary-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
          border-radius: 0.75rem;
          padding: 0.65rem 1rem;
          font-size: 0.875rem;
          font-weight: 600;
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(51 65 85);
        }
      `}</style>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        <Scale size={14} />
        {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900">{amount(value)}</div>
    </div>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="mx-6 mt-4 flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
      <AlertCircle size={16} />
      <span>{message}</span>
    </div>
  );
}

export default TrialBalance;
